/**
 * Store-facing starter on a sync run. Mirrors `syncRunStarterForStore` in
 * `src/types/syncRunStarter.ts`. A missing or junk value is no actor: the
 * trigger word stands alone, and `requestedBy` / `triggeredBy` are not read.
 */

export const SYNC_RUN_SYSTEM_SOURCES = ['schedule', 'webhook', 'follow_up', 'agent', 'api'] as const;

export type SyncRunSystemSource = (typeof SYNC_RUN_SYSTEM_SOURCES)[number];

export type ParsedSyncRunStarter =
  | { kind: 'store_user'; label: string | null }
  | { kind: 'motzu_admin' }
  | { kind: 'system'; source: SyncRunSystemSource };

const SYSTEM_LABEL_KEYS: Record<SyncRunSystemSource, string> = {
  schedule: 'integrations.syncRun.starter.schedule',
  webhook: 'integrations.syncRun.starter.webhook',
  follow_up: 'integrations.syncRun.starter.followUp',
  agent: 'integrations.syncRun.starter.agent',
  api: 'integrations.syncRun.starter.api',
};

function clean(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function parseSyncRunStarter(value: unknown): ParsedSyncRunStarter | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  if (raw.kind === 'motzu_admin') return { kind: 'motzu_admin' };
  if (raw.kind === 'system') {
    const source = raw.source;
    if (typeof source !== 'string' || !(SYNC_RUN_SYSTEM_SOURCES as readonly string[]).includes(source)) return null;
    return { kind: 'system', source: source as SyncRunSystemSource };
  }
  if (raw.kind === 'store_user') {
    return { kind: 'store_user', label: clean(raw.name) || clean(raw.email) };
  }
  return null;
}

/** Short label for the collapsed row and the Trigger value. Null when the run recorded nobody. */
export function syncRunStarterLabel(value: unknown, translate: (key: string) => string): string | null {
  const parsed = parseSyncRunStarter(value);
  if (!parsed) return null;
  if (parsed.kind === 'store_user') return parsed.label || translate('integrations.syncRun.starter.storeUser');
  if (parsed.kind === 'motzu_admin') return translate('integrations.syncRun.starter.motzuAdmin');
  return translate(SYSTEM_LABEL_KEYS[parsed.source]);
}
